import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AiBudgetApi, Plain, Rich } from '@agendor/shared';
import { Badge, Button, formatCurrency, vars } from '@agendor/ui';
import BusinessService, { PurchaseCreditPackError } from '../business-service';
import MonthNavigation from '../components/MonthNavigation';
import { Modal } from '../components/Modal';
import { useToast } from '../toast-context';

type CreditPurchaseModalProps = {
    service: BusinessService | null;
    creditCard: { last4: string; brand: string } | null;
    isOpen: boolean;
    onClose: () => void;
};

type Tab = 'buy' | 'history';

const TabButtons = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
`;

const TabButton = styled.button<{ $active: boolean }>`
    flex: 1;
    padding: 12px 16px;
    border: 1px solid ${(p) => (p.$active ? vars.foregroundColor : vars.dividerColor)};
    border-radius: 6px;
    background-color: ${(p) => (p.$active ? vars.primaryColorLight : 'transparent')};
    font: inherit;
    color: ${vars.foregroundColor};
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        border-color: ${vars.foregroundColor};
    }
`;

const Hero = styled.div`
    background-color: ${vars.primaryColorLight};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    margin-bottom: 16px;
`;

const HeroCredits = styled.span`
    font-size: 1.8rem;
    font-weight: 700;
    color: ${vars.foregroundColor};
`;

const HeroLabel = styled.span`
    font-size: 0.85rem;
    color: ${vars.foregroundColor};
`;

const Price = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const PriceValue = styled.span`
    font-size: 1.5rem;
    font-weight: 700;
    color: ${vars.foregroundColor};
`;

const PriceSub = styled.span`
    font-size: 0.9rem;
    color: #888;
`;

const PaymentMethod = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    margin-bottom: 12px;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
`;

const PaymentMethodTitle = styled.div`
    font-weight: 600;
    font-size: 1rem;
    color: ${vars.foregroundColor};
`;

const PaymentMethodCard = styled.div`
    font-size: 0.9rem;
    color: #666;
`;

const PayByInvoiceButton = styled.button`
    align-self: flex-start;
    padding: 0;
    background: none;
    border: none;
    font: inherit;
    font-size: 0.9rem;
    color: ${vars.accentColor};
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
        color: #888;
        cursor: default;
    }
`;

const Hint = styled.p`
    margin: 0 0 20px 0;
    font-size: 0.9rem;
    color: #888;
    line-height: 1.4;
`;

const MonthNavigationWrap = styled.div`
    margin-bottom: 16px;
    & > * {
        width: 100%;
    }
`;

const HistoryList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HistoryItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 6px;
`;

const HistoryItemMain = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const HistoryItemCredits = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: ${vars.foregroundColor};
`;

const HistoryItemDate = styled.span`
    font-size: 0.85rem;
    color: #888;
`;

const HistoryItemPrice = styled.span`
    color: ${vars.foregroundColor};
`;

const PopupBlockedNotice = styled.p`
    margin: 16px 0 0 0;
    padding: 12px;
    background-color: ${vars.warningTintSoft};
    border: 1px solid ${vars.warningColor};
    border-radius: 6px;
    font-size: 0.9rem;
    color: ${vars.foregroundColor};
    line-height: 1.4;

    a {
        color: ${vars.accentColor};
    }
`;

const HistoryStatus = styled.p`
    text-align: center;
    color: #888;
    margin: 24px 0;
    font-size: 0.95rem;
`;

const HistoryRetryButton = styled.button`
    display: block;
    margin: 16px auto 0;
    padding: 6px 14px;
    background: transparent;
    border: 1px solid ${vars.dividerColor};
    border-radius: 6px;
    color: ${vars.foregroundColor};
    font: inherit;
    cursor: pointer;

    &:hover {
        border-color: ${vars.foregroundColor};
    }
`;

const dayFormatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

function purchaseErrorMessage(error: unknown): string {
    if (!(error instanceof PurchaseCreditPackError)) return 'Não foi possível concluir a compra. Tente novamente.';
    switch (error.code) {
        case 'asaas_customer_not_found':
            return 'Cadastre seu CPF/CNPJ na tela de pagamento antes de comprar créditos.';
        case 'ai_budget_not_active':
            return 'Compra de créditos indisponível enquanto você não estiver em um plano com WhatsApp.';
        case 'card_declined':
            return 'O cartão foi recusado. Pague com pix ou boleto.';
        case 'no_credit_card':
            return 'Não há cartão salvo. Pague com pix ou boleto.';
    }
}

function plainDateToJsDate(date: Plain.SimpleDate): Date {
    return new Date(Date.UTC(date.year, date.month - 1, date.day));
}

export default function CreditPurchaseModal({ service, creditCard, isOpen, onClose }: CreditPurchaseModalProps) {
    const { showToast } = useToast();
    const [tab, setTab] = useState<Tab>('buy');
    const [purchasing, setPurchasing] = useState(false);
    const [month, setMonth] = useState<Plain.Month>(() => new Rich.Month(new Date()).plain);
    const [history, setHistory] = useState<AiBudgetApi.PackHistoryResponse | null>(null);
    const [historyError, setHistoryError] = useState(false);
    const [historyReloadNonce, setHistoryReloadNonce] = useState(0);
    const historyCacheRef = useRef<Map<string, AiBudgetApi.PackHistoryResponse>>(new Map());
    // Set when the popup is blocked: we keep the URL so the user can click an
    // anchor inside the modal (a real link click is never popup-blocked).
    const [pendingPaymentUrl, setPendingPaymentUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setTab('buy');
            setHistory(null);
            setHistoryError(false);
            setPendingPaymentUrl(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || tab !== 'history' || !service) return;
        const cacheKey = `${month.year}-${month.month}`;
        const cached = historyCacheRef.current.get(cacheKey);
        if (cached) {
            setHistory(cached);
            setHistoryError(false);
            return;
        }
        let cancelled = false;
        setHistory(null);
        setHistoryError(false);
        (async () => {
            try {
                const result = await service.getPackHistory(month);
                if (cancelled) return;
                historyCacheRef.current.set(cacheKey, result);
                setHistory(result);
            } catch (error) {
                console.error('Failed to load pack history', error);
                if (!cancelled) setHistoryError(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen, tab, month, service, historyReloadNonce]);

    const showPurchaseError = useCallback(
        (error: unknown) => {
            console.error('Failed to purchase credit pack', error);
            showToast(purchaseErrorMessage(error), 'error', 'modal');
        },
        [showToast],
    );

    const handleCardPurchase = useCallback(async () => {
        if (!service || purchasing) return;
        setPurchasing(true);
        try {
            const result = await service.purchaseCreditPackWithStoredCard();
            if (result.credited) {
                showToast(
                    `Créditos adicionados: cerca de ${AiBudgetApi.DEFAULT_PACK.approximateConversations} atendimentos a mais.`,
                    'success',
                );
            } else {
                showToast('Pagamento em processamento. Os créditos entram assim que ele for confirmado.', 'warning');
            }
            onClose();
        } catch (error) {
            showPurchaseError(error);
        } finally {
            setPurchasing(false);
        }
    }, [service, purchasing, onClose, showToast, showPurchaseError]);

    const handleInvoicePurchase = useCallback(async () => {
        if (!service || purchasing) return;
        // Pre-open synchronously so the browser still treats this as a
        // user-gesture window.open. We'll point it at the payment URL once
        // the API resolves, or close it if the call fails.
        const popup = window.open('about:blank', '_blank');
        setPurchasing(true);
        setPendingPaymentUrl(null);
        try {
            const result = await service.purchaseCreditPack();
            if (popup && !popup.closed) {
                popup.location.href = result.paymentUrl;
                onClose();
            } else {
                // Popup blocker swallowed the pre-open. Surface the URL inside
                // the modal so the user can click a real anchor.
                setPendingPaymentUrl(result.paymentUrl);
            }
        } catch (error) {
            popup?.close();
            showPurchaseError(error);
        } finally {
            setPurchasing(false);
        }
    }, [service, purchasing, onClose, showPurchaseError]);

    const price = formatCurrency(AiBudgetApi.DEFAULT_PACK.priceInCents);

    const footer =
        tab === 'buy' ? (
            <>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    primary
                    data-testid="credit-purchase-buy-btn"
                    onClick={creditCard ? handleCardPurchase : handleInvoicePurchase}
                    disabled={purchasing || !service}
                >
                    {purchasing ? (creditCard ? 'Cobrando…' : 'Abrindo…') : `Comprar por ${price}`}
                </Button>
            </>
        ) : (
            <Button onClick={onClose}>Fechar</Button>
        );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Comprar créditos de IA"
            footer={footer}
            data-testid="credit-purchase-modal"
        >
            <TabButtons>
                <TabButton
                    type="button"
                    $active={tab === 'buy'}
                    data-testid="credit-purchase-tab-buy"
                    onClick={() => setTab('buy')}
                >
                    Comprar
                </TabButton>
                <TabButton
                    type="button"
                    $active={tab === 'history'}
                    data-testid="credit-purchase-tab-history"
                    onClick={() => setTab('history')}
                >
                    Histórico
                </TabButton>
            </TabButtons>
            {tab === 'buy' ? (
                <>
                    <Hero>
                        <HeroCredits>~{AiBudgetApi.DEFAULT_PACK.approximateConversations}</HeroCredits>
                        <HeroLabel>atendimentos por recarga</HeroLabel>
                    </Hero>
                    <Price>
                        <PriceValue>{price}</PriceValue>
                        <PriceSub>pagamento único</PriceSub>
                    </Price>
                    {creditCard ? (
                        <>
                            <PaymentMethod data-testid="credit-purchase-card">
                                <PaymentMethodTitle>Pagamento no cartão</PaymentMethodTitle>
                                <PaymentMethodCard>
                                    Cartão {creditCard.brand} final {creditCard.last4}
                                </PaymentMethodCard>
                                <PayByInvoiceButton
                                    type="button"
                                    data-testid="credit-purchase-pay-by-invoice"
                                    onClick={handleInvoicePurchase}
                                    disabled={purchasing || !service}
                                >
                                    Pagar com pix ou boleto
                                </PayByInvoiceButton>
                            </PaymentMethod>
                            <Hint>
                                Ao comprar, cobramos {price} nesse cartão e os créditos entram na hora. Eles não expiram
                                com o ciclo — valem enquanto você estiver em um plano com WhatsApp.
                            </Hint>
                        </>
                    ) : (
                        <Hint>
                            Os créditos extras ficam disponíveis no momento do pagamento e não expiram com o ciclo —
                            valem enquanto você estiver em um plano com WhatsApp.
                        </Hint>
                    )}
                    {pendingPaymentUrl && (
                        <PopupBlockedNotice data-testid="credit-purchase-popup-blocked">
                            Seu navegador bloqueou a nova aba.{' '}
                            <a href={pendingPaymentUrl} target="_blank" rel="noreferrer">
                                Abrir página de pagamento
                            </a>
                            .
                        </PopupBlockedNotice>
                    )}
                </>
            ) : (
                <>
                    <MonthNavigationWrap>
                        <MonthNavigation
                            month={month}
                            onChange={setMonth}
                            prevTestId="credit-history-prev-month"
                            nextTestId="credit-history-next-month"
                        />
                    </MonthNavigationWrap>
                    {historyError ? (
                        <HistoryStatus data-testid="credit-history-error">
                            Não foi possível carregar o histórico.
                            <HistoryRetryButton
                                type="button"
                                data-testid="credit-history-retry"
                                onClick={() => setHistoryReloadNonce((n) => n + 1)}
                            >
                                Tentar novamente
                            </HistoryRetryButton>
                        </HistoryStatus>
                    ) : history === null ? (
                        <HistoryStatus>Carregando…</HistoryStatus>
                    ) : history.entries.length === 0 ? (
                        <HistoryStatus data-testid="credit-history-empty">Nenhuma compra neste mês.</HistoryStatus>
                    ) : (
                        <HistoryList data-testid="credit-purchase-history">
                            {history.entries.map((entry) => (
                                <HistoryItem key={entry.id} data-testid="credit-history-entry">
                                    <HistoryItemMain>
                                        <HistoryItemCredits>
                                            Recarga
                                            {entry.automatic && (
                                                <Badge tone="primary" data-testid="credit-history-automatic">
                                                    Automática
                                                </Badge>
                                            )}
                                        </HistoryItemCredits>
                                        <HistoryItemDate>
                                            {dayFormatter.format(plainDateToJsDate(entry.creditedAt))}
                                        </HistoryItemDate>
                                    </HistoryItemMain>
                                    <HistoryItemPrice>{formatCurrency(entry.priceInCents)}</HistoryItemPrice>
                                </HistoryItem>
                            ))}
                        </HistoryList>
                    )}
                </>
            )}
        </Modal>
    );
}
